import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { supabase } from "./integrations/supabase/client";
import ForceHTTPS from "./components/ForceHTTPS";

// Public Roofing Website Pages
import HomePage from "./pages/Home";
import ServicesPage from "./pages/public/Services";
import BrandsPage from "./pages/public/Brands";
import LocationsPage from "./pages/public/Locations";
import ServiceAreasPage from "./pages/public/ServiceAreas";
import PhotoGalleryPage from "./pages/PhotoGallery";
import PhotoAlbumPage from "./pages/PhotoAlbum";
import BlogPage from "./pages/public/Blog";
import ResourcesPage from "./pages/public/Resources";
import AboutUsPage from "./pages/public/AboutUs";
import ContactUsPage from "./pages/public/ContactUs";
import BookInspectionPage from "./pages/public/BookInspection";
import FinancingPage from "./pages/public/Financing";
import FAQPage from "./pages/public/FAQ";
import CareersPage from "./pages/public/Careers";
import PrivacyPage from "./pages/public/Privacy";
import SmsTermsPage from "./pages/public/SmsTerms";
import SitemapPage from "./pages/public/Sitemap";
import SitemapXMLFeed from "./pages/public/SitemapXMLFeed";
import SitemapIndex from "./pages/public/SitemapIndex";
import BlogSitemap from "./pages/public/BlogSitemap";
import LocationsSitemap from "./pages/public/LocationsSitemap";
import ImageSitemap from "./pages/public/ImageSitemap";
import RobotsTxt from "./pages/public/RobotsTxt";
import ChurchSteepleRepairPage from './pages/public/specialty/ChurchSteepleRepair';
import RoofCoatingPage from './pages/public/specialty/RoofCoating';
import ProfessionalChurchSteepleRepairRaleighNCPage from './pages/public/specialty/ProfessionalChurchSteepleRepairRaleighNC';

// All Location Pages - Alphabetical Import
import AftonPage from './pages/public/locations/Afton';
import AhoskiePage from './pages/public/locations/Ahoskie';
import AirliePage from './pages/public/locations/Airlie';
import AlertPage from './pages/public/locations/Alert';
import AngierPage from './pages/public/locations/Angier';
import ApexPage from './pages/public/locations/Apex';
import ArcherLodgePage from './pages/public/locations/ArcherLodge';
import AshevillePage from './pages/public/locations/Asheville';
import AulanderPage from './pages/public/locations/Aulander';
import AuroraPage from './pages/public/locations/Aurora';
import AutryvillePage from './pages/public/locations/Autryville';
import AvonPage from './pages/public/locations/Avon';
import AydenPage from './pages/public/locations/Ayden';
import BaileyPage from './pages/public/locations/Bailey';
import BathPage from './pages/public/locations/Bath';
import BattleboroPage from './pages/public/locations/Battleboro';
import BelhavenPage from './pages/public/locations/Belhaven';
import BellarthurPage from './pages/public/locations/Bellarthur';
import BensonPage from './pages/public/locations/Benson';
import BethelPage from './pages/public/locations/Bethel';
import BeulavillePage from './pages/public/locations/Beulaville';
import BlackCreekPage from './pages/public/locations/BlackCreek';
import BroadwayPage from './pages/public/locations/Broadway';
import BunnPage from './pages/public/locations/Bunn';
import BurgawPage from './pages/public/locations/Burgaw';
import BuxtonPage from './pages/public/locations/Buxton';
import CalypsoPage from './pages/public/locations/Calypso';
import CaryPage from './pages/public/locations/Cary';
import CastaliaPage from './pages/public/locations/Castalia';
import CentervillePage from './pages/public/locations/Centerville';
import ChapelHillPage from './pages/public/locations/ChapelHill';
import ChinquapinPage from './pages/public/locations/Chinquapin';
import ChocowinityPage from './pages/public/locations/Chocowinity';
import ClaytonPage from './pages/public/locations/Clayton';
import ClintonPage from './pages/public/locations/Clinton';
import CoatsPage from './pages/public/locations/Coats';
import ColerainPage from './pages/public/locations/Colerain';
import ColumbiaPage from './pages/public/locations/Columbia';
import ConetoePage from './pages/public/locations/Conetoe';
import ConwayPage from './pages/public/locations/Conway';
import CoveCityPage from './pages/public/locations/CoveCity';
import CreedmoorPage from './pages/public/locations/Creedmoor';
import CreswellPage from './pages/public/locations/Creswell';
import DeepRunPage from './pages/public/locations/DeepRun';
import DortchesPage from './pages/public/locations/Dortches';
import DoverPage from './pages/public/locations/Dover';
import DudleyPage from './pages/public/locations/Dudley';
import DunnPage from './pages/public/locations/Dunn';
import DurhamPage from './pages/public/locations/Durham';
import EdentonPage from './pages/public/locations/Edenton';
import ElizabethCityPage from './pages/public/locations/ElizabethCity';
import ElizabethtownPage from './pages/public/locations/Elizabethtown';
import ElmCityPage from './pages/public/locations/ElmCity';
import EnfieldPage from './pages/public/locations/Enfield';
import EngelhardPage from './pages/public/locations/Engelhard';
import ErwinPage from './pages/public/locations/Erwin';
import EurekaPage from './pages/public/locations/Eureka';
import FaisonPage from './pages/public/locations/Faison';
import FalklandPage from './pages/public/locations/Falkland';
import FarmvillePage from './pages/public/locations/Farmville';
import FayettevillePage from './pages/public/locations/Fayetteville';
import FountainPage from './pages/public/locations/Fountain';
import FourOaksPage from './pages/public/locations/FourOaks';
import FranklintonPage from './pages/public/locations/Franklinton';
import FremontPage from './pages/public/locations/Fremont';
import FuquayVarinaPage from './pages/public/locations/FuquayVarina';
import GarlandPage from './pages/public/locations/Garland';
import GarnerPage from './pages/public/locations/Garner';
import GarysburgPage from './pages/public/locations/Garysburg';
import GastonPage from './pages/public/locations/Gaston';
import GatesvillePage from './pages/public/locations/Gatesville';
import GodwinPage from './pages/public/locations/Godwin';
import GoldPointPage from './pages/public/locations/GoldPoint';
import GoldsboroPage from './pages/public/locations/Goldsboro';
import GreenvillePage from './pages/public/locations/Greenville';
import GreenvilleNCPage from './pages/public/locations/GreenvilleNC';
import GriftonPage from './pages/public/locations/Grifton';
import GrimeslandPage from './pages/public/locations/Grimesland';
import HalifaxPage from './pages/public/locations/Halifax';
import HamiltonPage from './pages/public/locations/Hamilton';
import HarrellsPage from './pages/public/locations/Harrells';
import HassellPage from './pages/public/locations/Hassell';
import HatterasPage from './pages/public/locations/Hatteras';
import HavelockPage from './pages/public/locations/Havelock';
import HendersonPage from './pages/public/locations/Henderson';
import HertfordPage from './pages/public/locations/Hertford';
import HickoryPage from './pages/public/locations/Hickory';
import HobgoodPage from './pages/public/locations/Hobgood';
import HollisterPage from './pages/public/locations/Hollister';
import HollySpringPage from './pages/public/locations/HollySpring';
import HookertonPage from './pages/public/locations/Hookerton';
import JacksonPage from './pages/public/locations/Jackson';
import JacksonvillePage from './pages/public/locations/Jacksonville';
import JamesvillePage from './pages/public/locations/Jamesville';
import KelfordPage from './pages/public/locations/Kelford';
import KenansvillePage from './pages/public/locations/Kenansville';
import KenlyPage from './pages/public/locations/Kenly';
import KillDevilHillsPage from './pages/public/locations/KillDevilHills';
import KinstonPage from './pages/public/locations/Kinston';
import KittyHawkPage from './pages/public/locations/KittyHawk';
import KnightdalePage from './pages/public/locations/Knightdale';
import LaGrangePage from './pages/public/locations/LaGrange';
import LeggettPage from './pages/public/locations/Leggett';
import LewistonPage from './pages/public/locations/Lewiston';
import LillingtonPage from './pages/public/locations/Lillington';
import LittletonPage from './pages/public/locations/Littleton';
import LocationTemplatePage from './pages/public/locations/LocationTemplate';
import LouisburgPage from './pages/public/locations/Louisburg';
import LucamaPage from './pages/public/locations/Lucama';
import LumbertonPage from './pages/public/locations/Lumberton';
import MacclesfieldPage from './pages/public/locations/Macclesfield';
import MaconPage from './pages/public/locations/Macon';
import MagnoliaPage from './pages/public/locations/Magnolia';
import ManteoPage from './pages/public/locations/Manteo';
import MaysvillePage from './pages/public/locations/Maysville';
import MicroPage from './pages/public/locations/Micro';
import MiddlesexPage from './pages/public/locations/Middlesex';
import MomeyerPage from './pages/public/locations/Momeyer';
import MoreheadCityPage from './pages/public/locations/MoreheadCity';
import MorrisvillePage from './pages/public/locations/Morrisville';
import MountOlivePage from './pages/public/locations/MountOlive';
import MurfreesboroPage from './pages/public/locations/Murfreesboro';
import NagsHeadPage from './pages/public/locations/NagsHead';
import NashvillePage from './pages/public/locations/Nashville';
import NewBernPage from './pages/public/locations/NewBern';
import NewtonGrovePage from './pages/public/locations/NewtonGrove';
import NorlinaPage from './pages/public/locations/Norlina';
import OakCityPage from './pages/public/locations/OakCity';
import OcracokePage from './pages/public/locations/Ocracoke';
import OxfordPage from './pages/public/locations/Oxford';
import PantegoPage from './pages/public/locations/Pantego';
import PikevillePage from './pages/public/locations/Pikeville';
import PineLevelPage from './pages/public/locations/PineLevel';
import PinetopsPage from './pages/public/locations/Pinetops';
import PinkHillPage from './pages/public/locations/PinkHill';
import PittsboroPage from './pages/public/locations/Pittsboro';
import PlymouthPage from './pages/public/locations/Plymouth';
import PollocksvillePage from './pages/public/locations/Pollocksville';
import PowellsvillePage from './pages/public/locations/Powellsville';
import PrincetonPage from './pages/public/locations/Princeton';
import RaleighPage from './pages/public/locations/Raleigh';
import RedOakPage from './pages/public/locations/RedOak';
import RichSquarePage from './pages/public/locations/RichSquare';
import RichlandsPage from './pages/public/locations/Richlands';
import RoanokeRapidsPage from './pages/public/locations/RoanokeRapids';
import RobersonvillePage from './pages/public/locations/Robersonville';
import RockyMountPage from './pages/public/locations/Rocky Mount';
import RockyMountNCPage from './pages/public/locations/RockyMountNC';
import RockyMountTemplateDemo from './pages/public/locations/RockyMountTemplate';
import RodanthePage from './pages/public/locations/Rodanthe';
import RolesvillePage from './pages/public/locations/Rolesville';
import RoperPage from './pages/public/locations/Roper';
import RoseHillPage from './pages/public/locations/RoseHill';
import RoseboroPage from './pages/public/locations/Roseboro';
import SalemburgPage from './pages/public/locations/Salemburg';
import SalvoPage from './pages/public/locations/Salvo';
import SanfordPage from './pages/public/locations/Sanford';
import SaratogaPage from './pages/public/locations/Saratoga';
import ScotlandNeckPage from './pages/public/locations/ScotlandNeck';
import SeaboardPage from './pages/public/locations/Seaboard';
import SelmaPage from './pages/public/locations/Selma';
import SevenSpringsPage from './pages/public/locations/SevenSprings';
import SharpsburgPage from './pages/public/locations/Sharpsburg';
import SimsPage from './pages/public/locations/Sims';
import SmithfieldPage from './pages/public/locations/Smithfield';
import SnowHillPage from './pages/public/locations/SnowHill';
import SpeedPage from './pages/public/locations/Speed';
import SpringHopePage from './pages/public/locations/SpringHope';
import StantonsburgPage from './pages/public/locations/Stantonsburg';
import StokesPage from './pages/public/locations/Stokes';
import SwanQuarterPage from './pages/public/locations/SwanQuarter';
import SwansboroPage from './pages/public/locations/Swansboro';
import TarboroPage from './pages/public/locations/Tarboro';
import TeacheyPage from './pages/public/locations/Teachey';
import ThelmaPage from './pages/public/locations/Thelma';
import TrentonPage from './pages/public/locations/Trenton';
import TurkeyPage from './pages/public/locations/Turkey';
import TynerPage from './pages/public/locations/Tyner';
import VanceboroPage from './pages/public/locations/Vanceboro';
import VaughanPage from './pages/public/locations/Vaughan';
import WadePage from './pages/public/locations/Wade';
import WakeForestPage from './pages/public/locations/WakeForest';
import WallacePage from './pages/public/locations/Wallace';
import WalstonburgPage from './pages/public/locations/Walstonburg';
import WanchesePage from './pages/public/locations/Wanchese';
import WardenPage from './pages/public/locations/Warden';
import WarrentonPage from './pages/public/locations/Warrenton';
import WarsawPage from './pages/public/locations/Warsaw';
import WashingtonPage from './pages/public/locations/Washington';
import WavesPage from './pages/public/locations/Waves';
import WaynesvillePage from './pages/public/locations/Waynesville';
import WeldonPage from './pages/public/locations/Weldon';
import WendellPage from './pages/public/locations/Wendell';
import WhitakersPage from './pages/public/locations/Whitakers';
import WhitevillePage from './pages/public/locations/Whiteville';
import WilliamstonPage from './pages/public/locations/Williamston';
import WilmingtonPage from './pages/public/locations/Wilmington';
import WilsonPage from './pages/public/locations/Wilson';
import WindsorPage from './pages/public/locations/Windsor';
import WinfallPage from './pages/public/locations/Winfall';
import WintervillePage from './pages/public/locations/Winterville';
import WintonPage from './pages/public/locations/Winton';
import WoodlandPage from './pages/public/locations/Woodland';
import YancevillePage from './pages/public/locations/Yanceyville';
import YoungSvillePage from './pages/public/locations/Youngsville';
import ZebulonPage from './pages/public/locations/Zebulon';

// Resource Pages Imports
import UnderstandingRoofingMaterialsPage from './pages/public/resources/UnderstandingRoofingMaterials';
import RoofReplacementProcessPage from './pages/public/resources/RoofReplacementProcess';
import IdentifyRoofDamagePage from './pages/public/resources/IdentifyRoofDamage';
import WorkmanshipWarrantyPage from './pages/public/resources/WorkmanshipWarranty';
import ManufacturerWarrantiesPage from './pages/public/resources/ManufacturerWarranties';
import WarrantyRegistrationPage from './pages/public/resources/WarrantyRegistration';
import InsuranceClaimProcessPage from './pages/public/resources/InsuranceClaimProcess';
import WorkingWithAdjustersPage from './pages/public/resources/WorkingWithAdjusters';
import DocumentationRequirementsPage from './pages/public/resources/DocumentationRequirements';
import GeneralRoofingFAQPage from './pages/public/resources/GeneralRoofingFAQ';
import RoofingMaterialsFAQPage from './pages/public/resources/RoofingMaterialsFAQ';
import RoofingCostsFAQPage from './pages/public/resources/RoofingCostsFAQ';
import RoofVentilationGuidePage from './pages/public/resources/RoofVentilationGuide';
import DesignYourRoofPage from './pages/public/DesignYourRoof';
import RoofLifeExpectancyReportPage from './pages/public/RoofLifeExpectancyReport';
import ResidentialRoofingPage from './pages/public/services/ResidentialRoofing';
import CommercialRoofingPage from './pages/public/services/CommercialRoofing';
import StormDamagePage from './pages/public/services/StormDamage';

// Admin Dashboard Pages
import Dashboard from "./pages/Dashboard";
import CreatePost from "./pages/CreatePost";
import Variables from "./pages/Variables";
import Posts from "./pages/Posts";
import PostPreview from "./pages/PostPreview";
import SEOAudit from "./pages/admin/SEOAudit";
import LinkChecker from "./pages/admin/LinkChecker";
import AdminPage from "./pages/public/Admin";
import NotFound from "./pages/NotFound";
import AuthPage from "./pages/Auth";

// SEO Components
import SEOManager from "./components/SEO/SEOManager";

// Layout Components
import ScrollToTop from "./components/Layout/ScrollToTop";

import RoofInstallationsAndMaterialsPage from './pages/public/RoofInstallationsAndMaterials';
import BestRooferNearMeGreenvilleNC from "./pages/public/BestRooferNearMeGreenvilleNC";

const queryClient = new QueryClient();

const App = () => {
  const [session, setSession] = useState<any>(null);
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ForceHTTPS />
          <ScrollToTop />
          <SEOManager />
          <Routes>
            {/* Public Roofing Company Website Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/home" element={<Navigate to="/" replace />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/brands" element={<BrandsPage />} />
            <Route path="/locations" element={<LocationsPage />} />
            <Route path="/service-areas" element={<ServiceAreasPage />} />
            <Route path="/locations/greenville-nc" element={<GreenvilleNCPage />} />
            <Route path="/locations/rockymount-nc" element={<RockyMountNCPage />} />
            <Route path="/gallery" element={<PhotoGalleryPage />} />
            <Route path="/photo-album/" element={user ? <PhotoAlbumPage /> : <Navigate to="/auth" replace />} />
            <Route path="/roof-installations-and-materials" element={<RoofInstallationsAndMaterialsPage />} />
            
            {/* Custom Landing Page Route */}
            <Route path="/best-roofer-near-me-in-greenville-nc-carolina-trophy-roofs" element={<BestRooferNearMeGreenvilleNC />} />
            
            {/* Blog Routes - location-aware */}
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPage />} />
            
            <Route path="/resources" element={<ResourcesPage />} />
            <Route path="/resources/understanding-roofing-materials" element={<UnderstandingRoofingMaterialsPage />} />
            <Route path="/resources/roof-replacement-process" element={<RoofReplacementProcessPage />} />
            <Route path="/resources/identify-roof-damage" element={<IdentifyRoofDamagePage />} />
            <Route path="/resources/workmanship-warranty" element={<WorkmanshipWarrantyPage />} />
            <Route path="/resources/manufacturer-warranties" element={<ManufacturerWarrantiesPage />} />
            <Route path="/resources/warranty-registration" element={<WarrantyRegistrationPage />} />
            <Route path="/resources/insurance-claim-process" element={<InsuranceClaimProcessPage />} />
            <Route path="/resources/working-with-adjusters" element={<WorkingWithAdjustersPage />} />
            <Route path="/resources/documentation-requirements" element={<DocumentationRequirementsPage />} />
            <Route path="/resources/general-roofing-faq" element={<GeneralRoofingFAQPage />} />
            <Route path="/resources/roofing-materials-faq" element={<RoofingMaterialsFAQPage />} />
            <Route path="/resources/roofing-costs-faq" element={<RoofingCostsFAQPage />} />
            <Route path="/resources/roof-ventilation-guide" element={<RoofVentilationGuidePage />} />
            <Route path="/about" element={<AboutUsPage />} />
            <Route path="/contact" element={<ContactUsPage />} />
            <Route path="/book-inspection" element={<BookInspectionPage />} />
            <Route path="/financing" element={<FinancingPage />} />
            <Route path="/frequently-asked-questions" element={<FAQPage />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/sms-terms" element={<SmsTermsPage />} />
            <Route path="/sitemap" element={<SitemapPage />} />
            <Route path="/sitemap-blog.xml" element={<BlogSitemap />} />
            
            <Route path="/robots.txt" element={<RobotsTxt />} />
            <Route path="/design-your-roof" element={<DesignYourRoofPage />} />
            <Route path="/roof-life-expectancy-report" element={<RoofLifeExpectancyReportPage />} />
            <Route path="/residential-roofing" element={<ResidentialRoofingPage />} />
            <Route path="/commercial-roofing" element={<CommercialRoofingPage />} />
            <Route path="/storm-damage" element={<StormDamagePage />} />
            <Route path="/location-template-demo" element={<RockyMountTemplateDemo />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/auth" element={<AuthPage />} />
            
            {/* All Location Routes */}
            <Route path="/locations/afton" element={<AftonPage />} />
            <Route path="/locations/ahoskie" element={<AhoskiePage />} />
            <Route path="/locations/airlie" element={<AirliePage />} />
            <Route path="/locations/alert" element={<AlertPage />} />
            <Route path="/locations/angier" element={<AngierPage />} />
            <Route path="/locations/apex" element={<ApexPage />} />
            <Route path="/locations/archer-lodge" element={<ArcherLodgePage />} />
            <Route path="/locations/asheville" element={<AshevillePage />} />
            <Route path="/locations/aulander" element={<AulanderPage />} />
            <Route path="/locations/aurora" element={<AuroraPage />} />
            <Route path="/locations/autryville" element={<AutryvillePage />} />
            <Route path="/locations/avon" element={<AvonPage />} />
            <Route path="/locations/ayden" element={<AydenPage />} />
            <Route path="/locations/bailey" element={<BaileyPage />} />
            <Route path="/locations/bath" element={<BathPage />} />
            <Route path="/locations/battleboro" element={<BattleboroPage />} />
            <Route path="/locations/belhaven" element={<BelhavenPage />} />
            <Route path="/locations/bellarthur" element={<BellarthurPage />} />
            <Route path="/locations/benson" element={<BensonPage />} />
            <Route path="/locations/bethel" element={<BethelPage />} />
            <Route path="/locations/beulaville" element={<BeulavillePage />} />
            <Route path="/locations/black-creek" element={<BlackCreekPage />} />
            <Route path="/locations/broadway" element={<BroadwayPage />} />
            <Route path="/locations/bunn" element={<BunnPage />} />
            <Route path="/locations/burgaw" element={<BurgawPage />} />
            <Route path="/locations/buxton" element={<BuxtonPage />} />
            <Route path="/locations/calypso" element={<CalypsoPage />} />
            <Route path="/locations/cary" element={<CaryPage />} />
            <Route path="/locations/castalia" element={<CastaliaPage />} />
            <Route path="/locations/centerville" element={<CentervillePage />} />
            <Route path="/locations/chapel-hill" element={<ChapelHillPage />} />
            <Route path="/locations/chinquapin" element={<ChinquapinPage />} />
            <Route path="/locations/chocowinity" element={<ChocowinityPage />} />
            <Route path="/locations/clayton" element={<ClaytonPage />} />
            <Route path="/locations/clinton" element={<ClintonPage />} />
            <Route path="/locations/coats" element={<CoatsPage />} />
            <Route path="/locations/colerain" element={<ColerainPage />} />
            <Route path="/locations/columbia" element={<ColumbiaPage />} />
            <Route path="/locations/conetoe" element={<ConetoePage />} />
            <Route path="/locations/conway" element={<ConwayPage />} />
            <Route path="/locations/cove-city" element={<CoveCityPage />} />
            <Route path="/locations/creedmoor" element={<CreedmoorPage />} />
            <Route path="/locations/creswell" element={<CreswellPage />} />
            <Route path="/locations/deep-run" element={<DeepRunPage />} />
            <Route path="/locations/dortches" element={<DortchesPage />} />
            <Route path="/locations/dover" element={<DoverPage />} />
            <Route path="/locations/dudley" element={<DudleyPage />} />
            <Route path="/locations/dunn" element={<DunnPage />} />
            <Route path="/locations/durham" element={<DurhamPage />} />
            <Route path="/locations/edenton" element={<EdentonPage />} />
            <Route path="/locations/elizabeth-city" element={<ElizabethCityPage />} />
            <Route path="/locations/elizabethtown" element={<ElizabethtownPage />} />
            <Route path="/locations/elm-city" element={<ElmCityPage />} />
            <Route path="/locations/enfield" element={<EnfieldPage />} />
            <Route path="/locations/engelhard" element={<EngelhardPage />} />
            <Route path="/locations/erwin" element={<ErwinPage />} />
            <Route path="/locations/eureka" element={<EurekaPage />} />
            <Route path="/locations/faison" element={<FaisonPage />} />
            <Route path="/locations/falkland" element={<FalklandPage />} />
            <Route path="/locations/farmville" element={<FarmvillePage />} />
            <Route path="/locations/fayetteville" element={<FayettevillePage />} />
            <Route path="/locations/fountain" element={<FountainPage />} />
            <Route path="/locations/four-oaks" element={<FourOaksPage />} />
            <Route path="/locations/franklinton" element={<FranklintonPage />} />
            <Route path="/locations/fremont" element={<FremontPage />} />
            <Route path="/locations/fuquay-varina" element={<FuquayVarinaPage />} />
            <Route path="/locations/garland" element={<GarlandPage />} />
            <Route path="/locations/garner" element={<GarnerPage />} />
            <Route path="/locations/garysburg" element={<GarysburgPage />} />
            <Route path="/locations/gaston" element={<GastonPage />} />
            <Route path="/locations/gatesville" element={<GatesvillePage />} />
            <Route path="/locations/godwin" element={<GodwinPage />} />
            <Route path="/locations/gold-point" element={<GoldPointPage />} />
            <Route path="/locations/goldsboro" element={<GoldsboroPage />} />
            <Route path="/locations/greenville" element={<GreenvillePage />} />
            <Route path="/locations/grifton" element={<GriftonPage />} />
            <Route path="/locations/grimesland" element={<GrimeslandPage />} />
            <Route path="/locations/halifax" element={<HalifaxPage />} />
            <Route path="/locations/hamilton" element={<HamiltonPage />} />
            <Route path="/locations/harrells" element={<HarrellsPage />} />
            <Route path="/locations/hassell" element={<HassellPage />} />
            <Route path="/locations/hatteras" element={<HatterasPage />} />
            <Route path="/locations/havelock" element={<HavelockPage />} />
            <Route path="/locations/henderson" element={<HendersonPage />} />
            <Route path="/locations/hertford" element={<HertfordPage />} />
            <Route path="/locations/hickory" element={<HickoryPage />} />
            <Route path="/locations/hobgood" element={<HobgoodPage />} />
            <Route path="/locations/hollister" element={<HollisterPage />} />
            <Route path="/locations/holly-spring" element={<HollySpringPage />} />
            <Route path="/locations/hookerton" element={<HookertonPage />} />
            <Route path="/locations/jackson" element={<JacksonPage />} />
            <Route path="/locations/jacksonville" element={<JacksonvillePage />} />
            <Route path="/locations/jamesville" element={<JamesvillePage />} />
            <Route path="/locations/kelford" element={<KelfordPage />} />
            <Route path="/locations/kenansville" element={<KenansvillePage />} />
            <Route path="/locations/kenly" element={<KenlyPage />} />
            <Route path="/locations/kill-devil-hills" element={<KillDevilHillsPage />} />
            <Route path="/locations/kinston" element={<KinstonPage />} />
            <Route path="/locations/kitty-hawk" element={<KittyHawkPage />} />
            <Route path="/locations/knightdale" element={<KnightdalePage />} />
            <Route path="/locations/la-grange" element={<LaGrangePage />} />
            <Route path="/locations/leggett" element={<LeggettPage />} />
            <Route path="/locations/lewiston" element={<LewistonPage />} />
            <Route path="/locations/lillington" element={<LillingtonPage />} />
            <Route path="/locations/littleton" element={<LittletonPage />} />
            
            <Route path="/locations/louisburg" element={<LouisburgPage />} />
            <Route path="/locations/lucama" element={<LucamaPage />} />
            <Route path="/locations/lumberton" element={<LumbertonPage />} />
            <Route path="/locations/macclesfield" element={<MacclesfieldPage />} />
            <Route path="/locations/macon" element={<MaconPage />} />
            <Route path="/locations/magnolia" element={<MagnoliaPage />} />
            <Route path="/locations/manteo" element={<ManteoPage />} />
            <Route path="/locations/maysville" element={<MaysvillePage />} />
            <Route path="/locations/micro" element={<MicroPage />} />
            <Route path="/locations/middlesex" element={<MiddlesexPage />} />
            <Route path="/locations/momeyer" element={<MomeyerPage />} />
            <Route path="/locations/morehead-city" element={<MoreheadCityPage />} />
            <Route path="/locations/morrisville" element={<MorrisvillePage />} />
            <Route path="/locations/mount-olive" element={<MountOlivePage />} />
            <Route path="/locations/murfreesboro" element={<MurfreesboroPage />} />
            <Route path="/locations/nags-head" element={<NagsHeadPage />} />
            <Route path="/locations/nashville" element={<NashvillePage />} />
            <Route path="/locations/new-bern" element={<NewBernPage />} />
            <Route path="/locations/newton-grove" element={<NewtonGrovePage />} />
            <Route path="/locations/norlina" element={<NorlinaPage />} />
            <Route path="/locations/oak-city" element={<OakCityPage />} />
            <Route path="/locations/ocracoke" element={<OcracokePage />} />
            <Route path="/locations/oxford" element={<OxfordPage />} />
            <Route path="/locations/pantego" element={<PantegoPage />} />
            <Route path="/locations/pikeville" element={<PikevillePage />} />
            <Route path="/locations/pine-level" element={<PineLevelPage />} />
            <Route path="/locations/pinetops" element={<PinetopsPage />} />
            <Route path="/locations/pink-hill" element={<PinkHillPage />} />
            <Route path="/locations/pittsboro" element={<PittsboroPage />} />
            <Route path="/locations/plymouth" element={<PlymouthPage />} />
            <Route path="/locations/pollocksville" element={<PollocksvillePage />} />
            <Route path="/locations/powellsville" element={<PowellsvillePage />} />
            <Route path="/locations/princeton" element={<PrincetonPage />} />
            <Route path="/locations/raleigh" element={<RaleighPage />} />
            <Route path="/locations/red-oak" element={<RedOakPage />} />
            <Route path="/locations/rich-square" element={<RichSquarePage />} />
            <Route path="/locations/richlands" element={<RichlandsPage />} />
            <Route path="/locations/roanoke-rapids" element={<RoanokeRapidsPage />} />
            <Route path="/locations/robersonville" element={<RobersonvillePage />} />
            <Route path="/locations/rocky-mount" element={<RockyMountPage />} />
            <Route path="/locations/rodanthe" element={<RodanthePage />} />
            <Route path="/locations/rolesville" element={<RolesvillePage />} />
            <Route path="/locations/roper" element={<RoperPage />} />
            <Route path="/locations/rose-hill" element={<RoseHillPage />} />
            <Route path="/locations/roseboro" element={<RoseboroPage />} />
            <Route path="/locations/salemburg" element={<SalemburgPage />} />
            <Route path="/locations/salvo" element={<SalvoPage />} />
            <Route path="/locations/sanford" element={<SanfordPage />} />
            <Route path="/locations/saratoga" element={<SaratogaPage />} />
            <Route path="/locations/scotland-neck" element={<ScotlandNeckPage />} />
            <Route path="/locations/seaboard" element={<SeaboardPage />} />
            <Route path="/locations/selma" element={<SelmaPage />} />
            <Route path="/locations/seven-springs" element={<SevenSpringsPage />} />
            <Route path="/locations/sharpsburg" element={<SharpsburgPage />} />
            <Route path="/locations/sims" element={<SimsPage />} />
            <Route path="/locations/smithfield" element={<SmithfieldPage />} />
            <Route path="/locations/snow-hill" element={<SnowHillPage />} />
            <Route path="/locations/speed" element={<SpeedPage />} />
            <Route path="/locations/spring-hope" element={<SpringHopePage />} />
            <Route path="/locations/stantonsburg" element={<StantonsburgPage />} />
            <Route path="/locations/stokes" element={<StokesPage />} />
            <Route path="/locations/swan-quarter" element={<SwanQuarterPage />} />
            <Route path="/locations/swansboro" element={<SwansboroPage />} />
            <Route path="/locations/tarboro" element={<TarboroPage />} />
            <Route path="/locations/teachey" element={<TeacheyPage />} />
            <Route path="/locations/thelma" element={<ThelmaPage />} />
            <Route path="/locations/trenton" element={<TrentonPage />} />
            <Route path="/locations/turkey" element={<TurkeyPage />} />
            <Route path="/locations/tyner" element={<TynerPage />} />
            <Route path="/locations/vanceboro" element={<VanceboroPage />} />
            <Route path="/locations/vaughan" element={<VaughanPage />} />
            <Route path="/locations/wade" element={<WadePage />} />
            <Route path="/locations/wake-forest" element={<WakeForestPage />} />
            <Route path="/locations/wallace" element={<WallacePage />} />
            <Route path="/locations/walstonburg" element={<WalstonburgPage />} />
            <Route path="/locations/wanchese" element={<WanchesePage />} />
            <Route path="/locations/warden" element={<WardenPage />} />
            <Route path="/locations/warrenton" element={<WarrentonPage />} />
            <Route path="/locations/warsaw" element={<WarsawPage />} />
            <Route path="/locations/washington" element={<WashingtonPage />} />
            <Route path="/locations/waves" element={<WavesPage />} />
            <Route path="/locations/waynesville" element={<WaynesvillePage />} />
            <Route path="/locations/weldon" element={<WeldonPage />} />
            <Route path="/locations/wendell" element={<WendellPage />} />
            <Route path="/locations/whitakers" element={<WhitakersPage />} />
            <Route path="/locations/whiteville" element={<WhitevillePage />} />
            <Route path="/locations/williamston" element={<WilliamstonPage />} />
            <Route path="/locations/wilmington" element={<WilmingtonPage />} />
            <Route path="/locations/wilson" element={<WilsonPage />} />
            <Route path="/locations/windsor" element={<WindsorPage />} />
            <Route path="/locations/winfall" element={<WinfallPage />} />
            <Route path="/locations/winterville" element={<WintervillePage />} />
            <Route path="/locations/winton" element={<WintonPage />} />
            <Route path="/locations/woodland" element={<WoodlandPage />} />
            <Route path="/locations/yanceyville" element={<YancevillePage />} />
            <Route path="/locations/youngsville" element={<YoungSvillePage />} />
            <Route path="/locations/zebulon" element={<ZebulonPage />} />
            
            {/* Specialty Services Routes */}
            <Route path="/specialty-services/church-steeple-repair" element={<ChurchSteepleRepairPage />} />
            <Route path="/specialty-services/roof-coating" element={<RoofCoatingPage />} />
            <Route path="/professional-church-steeple-repair-and-replacement-services-in-raleigh-nc-and-surrounding-areas" element={<ProfessionalChurchSteepleRepairRaleighNCPage />} />
            
            {/* Admin Dashboard Routes */}
            <Route path="/Blog-Page-Builder" element={user ? <Dashboard /> : <Navigate to="/auth" replace />} />
            <Route path="/create-post" element={user ? <CreatePost /> : <Navigate to="/auth" replace />} />
            <Route path="/variables" element={user ? <Variables /> : <Navigate to="/auth" replace />} />
            <Route path="/posts" element={user ? <Posts /> : <Navigate to="/auth" replace />} />
            <Route path="/post-preview/:id" element={user ? <PostPreview /> : <Navigate to="/auth" replace />} />
            <Route path="/seo-audit" element={user ? <SEOAudit /> : <Navigate to="/auth" replace />} />
            <Route path="/link-checker" element={user ? <LinkChecker /> : <Navigate to="/auth" replace />} />
            
            {/* 404 Route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
